import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtINR } from "@/lib/format";
import { ShieldAlert, TrendingDown, RefreshCw, Info } from "lucide-react";

/**
 * VendorGstr3bHealthCard · Iter-121
 * Shows the % of the org's input-ITC (from GSTR-2B) that is at risk because
 * the SUPPLIER hasn't filed their own GSTR-3B for the same period. Auto-picks
 * the last completed month. Data source: /api/gst-reports/vendor-3b-health
 */
export default function VendorGstr3bHealthCard({ gstin }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    if (!gstin) return;
    setBusy(true); setErr("");
    try {
      const r = await api.get("/gst-reports/vendor-3b-health", { params: { gstin } });
      setD(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Health lookup failed — check the gst.gov.in login in Zenith360-GST.");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [gstin]);

  if (!gstin) return null;
  if (busy && !d) return (
    <div className="card-sharp p-5" data-testid="vendor-3b-health-card">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1">
        <RefreshCw className="w-3 h-3 animate-spin" /> Vendor 3B Health · loading…
      </div>
    </div>
  );

  const pct = d?.at_risk_pct ?? 0;
  const tone = pct === 0 ? "safe" : pct < 10 ? "watch" : pct < 25 ? "warn" : "danger";
  const toneClasses = {
    safe:   { border: "border-emerald-500/30", bar: "bg-emerald-500", text: "text-emerald-600" },
    watch:  { border: "border-amber-500/30",   bar: "bg-amber-500",   text: "text-amber-600" },
    warn:   { border: "border-orange-500/30",  bar: "bg-orange-500",  text: "text-orange-600" },
    danger: { border: "border-red-500/40",     bar: "bg-red-500",     text: "text-red-600" },
  }[tone];

  return (
    <div className={`card-sharp p-5 relative overflow-hidden border-l-4 ${toneClasses.border}`}
          data-testid="vendor-3b-health-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            Vendor GSTR-3B Health · {d?.period_label || "—"}
          </div>
          <div className="metric-value text-3xl mt-2" data-testid="vendor-3b-pct">
            {err ? "—" : `${pct.toFixed(1)}%`}
          </div>
          <div className={`text-xs mt-2 ${toneClasses.text}`}>
            {err ? err : (
              pct === 0
                ? "All vendors have filed 3B · ITC safe"
                : `${fmtINR(d?.at_risk_itc || 0)} at-risk from ${d?.vendors_unfiled || 0} of ${d?.vendors_total || 0} vendors`
            )}
          </div>
        </div>
        <div className={`w-9 h-9 rounded-sm flex items-center justify-center bg-muted ${toneClasses.text}`}>
          {tone === "safe" ? <Info className="w-4 h-4" /> : <ShieldAlert className="w-4 h-4" />}
        </div>
      </div>

      {!err && d?.total_itc > 0 && (
        <div className="w-full h-1.5 bg-muted rounded-sm overflow-hidden mb-3" data-testid="vendor-3b-bar">
          <div className={`h-full ${toneClasses.bar} transition-all`}
                  style={{ width: `${Math.min(pct, 100)}%` }} />
        </div>
      )}

      {(d?.top_offenders || []).length > 0 && (
        <div className="space-y-1 text-[11px]">
          <div className="text-muted-foreground flex items-center gap-1">
            <TrendingDown className="w-3 h-3" /> Top ITC-risk suppliers
          </div>
          {d.top_offenders.slice(0, 3).map((o) => (
            <div key={o.gstin} className="flex items-center justify-between py-0.5 border-b border-border/30 last:border-none"
                    data-testid={`vendor-3b-offender-${o.gstin.slice(0, 8)}`}>
              <span className="truncate flex-1 mr-2">
                <span className="text-foreground/80">{o.name || o.gstin}</span>
                <span className="ml-1 font-mono text-[10px] text-muted-foreground">{o.gstin}</span>
              </span>
              <span className={`font-mono text-[10px] ${toneClasses.text} shrink-0`}>{fmtINR(o.itc_at_risk)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
